import { createSlice } from "@reduxjs/toolkit";
import chatService from "./../../../services/chatService/chatService";

const chatSlice = createSlice({
    name: "chat",
    initialState: {
        user: null,
        admins: [],
        onlineUsers: [],
        typingInfo: [],
        adminTypingInfo: [],
        pairedAdmin: null,
        usersWithMessages: [],
        allUsersWithMessages: [], // Full list
    },

    reducers: {

        setUser(state, action) {
            state.user = action.payload;
        },

        addMessage(state, action) {
            state.messages.push(action.payload);
        },

        // setTyping(state, action) {
        //     state.typingInfo = action.payload;
        // },

        setAdminTyping(state, action) {
            const { adminEmail, userEmail, type } = action.payload;
            if (!adminEmail || !userEmail || !type) return;

            if (!Array.isArray(state.adminTypingInfo)) state.adminTypingInfo = [];

            // Check if same object already exists
            const exists = state.adminTypingInfo.some(
                info => info.adminEmail === adminEmail && info.userEmail === userEmail && info.type === type
            );

            if (!exists) {
                state.adminTypingInfo.push({ adminEmail, userEmail, type });
            }
        },

        stopAdminTyping(state, action) {
            const { adminEmail, userEmail, type } = action.payload;
            if (!adminEmail || !userEmail || !type) return;

            if (!Array.isArray(state.adminTypingInfo)) return;

            // Filter out the object that matches
            state.adminTypingInfo = state.adminTypingInfo.filter(
                info => !(info.adminEmail === adminEmail && info.userEmail === userEmail && info.type === type)
            );
        },


        setTyping(state, action) {
            const email = action.payload?.email;
            if (!email) return; // যদি email না আসে, কিছু না করা

            // যদি typingInfo array না থাকে, empty array দিয়ে শুরু
            if (!Array.isArray(state.typingInfo)) state.typingInfo = [];

            // duplicate চেক করে push করা
            if (!state.typingInfo.includes(email)) {
                state.typingInfo.push(email);
            }
        },


        stopTyping(state, action) {
            const email = action.payload?.email;
            if (email) {
                // specific user stop typing
                state.typingInfo = state.typingInfo.filter(e => e !== email);
            } else {
                // all users stop typing
                state.typingInfo = [];
            }
        },

        setOnlineUsers(state, action) {
            const { type, data } = action.payload || {};
            //  console.log("slice : " , type)

            if (type === "onlineUsers") {
                // console.log("slice : " , data)
                state.onlineUsers = Array.isArray(data) ? data : [];
            }
            else {
                if (data) {
                    state.onlineUsers = [...state.onlineUsers, ...data];
                }
            }
        },


        setPairedAdmin(state, action) {
            state.pairedAdmin = action.payload;
        },



        setUsersWithMessages(state, action) {
            const incomingUsers = action.payload.map(u => ({
                ...u,
                lastMessage: u.lastMessage || null,
                unseenCount: u.unseenCount || 0,
            }));

            // Map existing users by _id
            const usersMap = new Map();
            state.usersWithMessages.forEach(u => usersMap.set(u._id, u));

            // Upsert incoming users
            incomingUsers.forEach(u => usersMap.set(u._id, u));

            // Convert map back to array
            const updatedUsers = Array.from(usersMap.values());

            // Sort by lastMessage.createdAt descending (newest first)
            updatedUsers.sort((a, b) => {
                const dateA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt) : new Date(0);
                const dateB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt) : new Date(0);
                return dateB - dateA;
            });

            // Update state
            state.usersWithMessages = updatedUsers;
            state.allUsersWithMessages = updatedUsers;
        },

        setDisconnectUsersWithMessages(state, action) {
            const incomingUsers = action.payload.map(u => ({
                ...u,
                lastMessage: u.lastMessage || null,
                unseenCount: u.unseenCount || 0,
            }));

            // Create a map of incoming users by _id
            const incomingMap = new Map();
            incomingUsers.forEach(u => incomingMap.set(u._id, u));

            // Keep only users that exist in incomingUsers (remove non-matching)
            const filteredUsers = state.usersWithMessages
                .filter(u => incomingMap.has(u._id));

            // Merge incoming users (update existing or add new)
            incomingUsers.forEach(u => {
                const index = filteredUsers.findIndex(fu => fu._id === u._id);
                if (index > -1) {
                    filteredUsers[index] = u; // update existing
                } else {
                    filteredUsers.push(u); // add new
                }
            });

            // Sort by lastMessage.createdAt descending
            filteredUsers.sort((a, b) => {
                const dateA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt) : new Date(0);
                const dateB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt) : new Date(0);
                return dateB - dateA;
            });

            // Update state
            state.usersWithMessages = filteredUsers;
            state.allUsersWithMessages = filteredUsers;
        },

        setUsersWithMessagesEmpty(state, action) {

            const data = action.payload;
            if (data?.length !== 0) return;
            // Update state
            state.usersWithMessages = data;
            state.allUsersWithMessages = data;
        },


        updateUserMessage(state, action) {
            const msg = action.payload;

            if (!msg) return;
            const userIdToUpdate = msg.userId?._id;

            const idx = state.usersWithMessages.findIndex(
                u => u._id === userIdToUpdate
            );

            if (idx === -1) return;

            const usr = state.usersWithMessages[idx];

            if (usr.lastMessage && usr.lastMessage._id === msg._id) {
                return;
            }

            let newUnseen = usr.unseenCount || 0;

            if (
                msg.isAdminSeen === false &&
                msg.type?.toLowerCase() === "user"
            ) {
                newUnseen += 1;
            }

            const updatedUser = {
                ...usr,
                lastMessage: msg,
                unseenCount: newUnseen,
            };

            state.usersWithMessages.splice(idx, 1);
            state.usersWithMessages.unshift(updatedUser);
        },


        updateUserAllMessageSeen(state, action) {
            const user = action.payload;
            if (!user) return;
            const userIdToUpdate = user?._id;
            if (!userIdToUpdate) return;
            const idx = state.usersWithMessages.findIndex(u => u?._id === userIdToUpdate);
            if (idx === -1) return;

            state.usersWithMessages[idx] = user;
        },


        UserSingleMessageSeenUpdate(state, action) {
            const msg = action.payload;
            if (!msg) return;
            const userIdToUpdate = msg.userId?._id;

            const idx = state.usersWithMessages.findIndex(
                u => u._id === userIdToUpdate
            );

            if (idx === -1) return;

            const usr = state.usersWithMessages[idx];

            if (usr.lastMessage && usr.lastMessage._id === msg._id) {
                return;
            }

            const updatedUser = {
                ...usr,
                lastMessage: msg,
                unseenCount: 0,
            };

            state.usersWithMessages.splice(idx, 1);
            state.usersWithMessages.unshift(updatedUser);
        },

        searchUsersWithMessages(state, action) {
            const keyword = action.payload?.toLowerCase() || "";
            if (!keyword) {
                state.usersWithMessages = state.allUsersWithMessages;
                return;
            }
            state.usersWithMessages = state.allUsersWithMessages.filter(obj =>
                Object.values(obj).some(value =>
                    String(value).toLowerCase().includes(keyword)
                )
            );
        }



    }
});

export const {
    setUser,
    addMessage,
    adminTypingInfo,
    setAdminTyping,
    stopAdminTyping,
    setTyping,
    stopTyping,
    setOnlineUsers,
    setPairedAdmin,
    setUsersWithMessages,
    updateUserMessage,
    decreaseUpdateUserMessage,
    setDisconnectUsersWithMessages,
    setUsersWithMessagesEmpty,
    searchUsersWithMessages,
    updateUserAllMessageSeen,
    UserSingleMessageSeenUpdate
} = chatSlice.actions;

export default chatSlice.reducer;

